import _ from 'lodash';
import {
  Program,
  WithOptionalId,
  AssignedProgram,
  Member,
  PagedResponse,
} from '../../types';
import { apiBaseUrl } from '../apiConsts';
import { getDefaultError } from '../../utils';
import { getDataWithRetry, mapPagedResult, sendWithoutBody } from '../fetchUtils';
import {
  isValidProgramResponse,
  isValidProgramsResponse,
} from '../apiValidators';
import { updateMemberData, fetchMembersByIds } from './members-controller';

import type { FetchResult, FetchSuccess } from '../fetchUtils';
import { AppError } from '../../error';

class ProgramAssignmentError extends Error {
  error: AppError;
  status?: number;
  durationMs?: number;

  constructor({
    error,
    status,
    durationMs,
  }: {
    error: AppError;
    status?: number;
    durationMs?: number;
  }) {
    super(error.message);
    this.name = 'ProgramAssignmentError';
    this.error = error;
    this.status = status;
    this.durationMs = durationMs;
  }
}

const createSuccessResult = <T>(data: T): FetchSuccess<T> => ({
  success: true,
  data,
  status: 200,
  durationMs: 0,
});

export const createNewProgram = async (
  program: WithOptionalId<Program>,
  trainerId: string,
): Promise<FetchResult<Program | null>> => {
  if (!trainerId) {
    return {
      data: null,
      success: false,
      error: getDefaultError('Trainer id must be provided'),
    };
  }
  const createResponse = await getDataWithRetry<Program | null>(
    `${apiBaseUrl}programs`,
    {
      headers: {
        'content-type': 'application/json',
      },
      method: 'POST',
      body: JSON.stringify(program),
    },
    isValidProgramResponse,
  );
  if (!createResponse.success || !createResponse.data) {
    return createResponse;
  }
  const associateProgramResult = await sendWithoutBody(
    `${apiBaseUrl}trainers/${trainerId}/associate/program`,
    {
      headers: { 'content-type': 'application/json' },
      method: 'POST',
      body: JSON.stringify([createResponse.data.id]),
    },
  );
  if (!associateProgramResult.success) {
    return associateProgramResult;
  }
  return {
    ...createResponse,
    data: {
      ...createResponse.data,
      trainerId,
    },
  };
};

export const updateProgramData = (
  program: Program,
): Promise<FetchResult<Program | null>> => {
  return getDataWithRetry(
    `${apiBaseUrl}programs`,
    {
      headers: { 'content-type': 'application/json' },
      method: 'PUT',
      body: JSON.stringify(program),
    },
    isValidProgramResponse,
  );
};

export const deleteProgramById = (
  programId: string,
): Promise<FetchResult<null>> => {
  return sendWithoutBody(`${apiBaseUrl}programs/${programId}`, {
    method: 'DELETE',
  });
};

export const fetchTrainerPrograms = async (
  trainerId: string,
): Promise<FetchResult<Program[]>> => {
  const response = await getDataWithRetry<PagedResponse<Program>>(
    `${apiBaseUrl}programs/getByTrainerIds?page=0&size=1000&sort=name`,
    {
      headers: { 'content-type': 'application/json' },
      method: 'POST',
      body: JSON.stringify([trainerId]),
    },
    isValidProgramsResponse,
  );
  return mapPagedResult(response);
};

export const assignProgramToMembers = async (
  memberIds: string[],
  program: AssignedProgram,
): Promise<FetchResult<Member[]>> => {
  if (!memberIds.length) {
    return createSuccessResult<Member[]>([]);
  }
  const membersResponse = await fetchMembersByIds(memberIds);
  if (!membersResponse.success || !membersResponse.data) {
    return {
      success: false,
      data: null,
      error:
        membersResponse.error ||
        getDefaultError('Failed to fetch members for assignment'),
      status: membersResponse.status,
      durationMs: membersResponse.durationMs,
    };
  }
  const membersById = _.keyBy(membersResponse.data, 'id');

  try {
    const updatedMembers = await Promise.all(
      memberIds.map(async (memberId) => {
        const member = membersById[memberId];
        if (!member) {
          throw new ProgramAssignmentError({
            error: getDefaultError(`Member ${memberId} not found`),
          });
        }
        const updateResult = await updateMemberData(member, {
          ...member,
          program,
        });
        if (!updateResult.success || !updateResult.data) {
          throw new ProgramAssignmentError({
            error:
              updateResult.error ||
              getDefaultError('Failed to assign program to member'),
            status: updateResult.status,
            durationMs: updateResult.durationMs,
          });
        }
        return updateResult.data;
      }),
    );

    return createSuccessResult<Member[]>(updatedMembers);
  } catch (failure) {
    const typedFailure =
      failure instanceof ProgramAssignmentError ? failure : null;
    return {
      success: false,
      data: null,
      error:
        typedFailure?.error ??
        getDefaultError('Failed to assign program to member'),
      status: typedFailure?.status,
      durationMs: typedFailure?.durationMs,
    };
  }
};
